/**
 * Supabase Admin Login Component
 * Production-grade admin authentication
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { 
  Shield, 
  Lock, 
  Mail, 
  AlertTriangle, 
  Eye, 
  EyeOff,
  Loader2
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { DPLIcon } from '@/components/brand/DPLLogo'

export function SupabaseAdminLogin() {
  const navigate = useNavigate()
  
  // All hooks must be called at the top
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  // Check if already authenticated and redirect to dashboard
  useEffect(() => {
    const adminUIState = localStorage.getItem('admin_ui_state')
    if (adminUIState) {
      try {
        const sessionData = JSON.parse(adminUIState)
        if (sessionData?.user) {
          navigate('/admin', { replace: true })
        }
      } catch (error) {
        localStorage.removeItem('admin_ui_state')
      }
    }
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        setError(error.message)
        toast.error('Login failed')
      } else {
        // Query the database-level role type
        const { data: role, error: roleError } = await supabase.rpc('get_user_role_type');
        
        if (roleError) {
          // Immediately sign out and clear state to avoid partial/inconsistent authentication
          await supabase.auth.signOut();
          localStorage.removeItem('admin_ui_state');
          setError('Unable to verify administrator privileges. Please try again.');
          toast.error('Verification failed');
          return;
        }

        if (role !== 'admin') {
          // Reject immediately, sign out, and clear UI cache
          await supabase.auth.signOut();
          localStorage.removeItem('admin_ui_state');
          setError('Access denied: You do not have administrator privileges.');
          toast.error('Access denied');
          return;
        }

        // Store non-sensitive metadata only for UI/layout rendering
        const adminUIState = {
          user: {
            id: data.user?.id,
            email: data.user?.email || email,
            role: role, // 'admin' directly from database
            is_active: true
          },
          expires_at: data.session?.expires_at
        }
        localStorage.setItem('admin_ui_state', JSON.stringify(adminUIState))

        toast.success('Login successful')
        navigate('/admin')
      }
    } catch (err) {
      setError('An unexpected error occurred')
      toast.error('Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Branding */}
        <div className="flex flex-col items-center mb-8">
          <DPLIcon className="h-12 w-12 mb-4" />
          <h1 className="text-2xl font-medium text-foreground mb-2">DCode DEW Admin</h1>
          <p className="text-muted-foreground text-center">
            Sign in to access the administration panel
          </p>
          <Badge className="mt-2 bg-purple-100 text-purple-800">
            <Shield className="h-3 w-3 mr-1" />
            Master Admin Portal
          </Badge>
        </div>

        {/* Login Form */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-foreground">Admin Sign In</CardTitle>
            <CardDescription className="text-muted-foreground">
              Enter your admin credentials to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">
                  <Mail className="h-4 w-4 mr-2 inline" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="bg-slate-700 border-slate-600 text-foreground placeholder-slate-400"
                  required
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">
                  <Lock className="h-4 w-4 mr-2 inline" />
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="bg-slate-700 border-slate-600 text-foreground placeholder-slate-400 pr-10"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Error Alert */}
              {error && (
                <Alert className="bg-red-900 border-red-800 text-red-200">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Sign In to Admin Panel
                  </>
                )}
              </Button>
            </form>

            {/* Security Notice */}
            <div className="mt-6 p-3 bg-slate-700 rounded-lg border border-slate-600">
              <div className="flex items-start gap-2">
                <Shield className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-slate-300">
                  <p className="font-medium text-foreground mb-1">Security Notice</p>
                  <p>
                    This is a secure admin portal. All access attempts are logged and monitored.
                    Unauthorized access attempts will be blocked.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>
            Need help? Contact your system administrator
          </p>
        </div>
      </div>
    </div>
  )
}